import "$styles/index.css"
import "$styles/syntax-highlighting.css"

// Import all JavaScript & CSS files from src/_components
import components from "$components/**/*.{js,jsx,js.rb,css}"

console.info("Bridgetown is loaded!")

if (["/get-started/", "/en/getting-started/"].includes(window.location.pathname) && ["#spaces", "#teams"].includes(window.location.hash)) {
  window.location.replace(`${window.location.hash === "#spaces" ? "/docs/en/spaces/" : "/docs/en/teams/"}`)
}

const previewButtons = document.querySelectorAll(".gallery-preview")

if (previewButtons.length > 0) {
  const previewVideos = Array.from(previewButtons)
    .map((button) => button.querySelector(".gallery-preview-media video"))
    .filter(Boolean)
  const previewItems = previewVideos.map((video) => ({
    video,
    button: video.closest(".gallery-preview"),
    ratio: 0,
    lastFocusAt: 0
  }))
  let activePreviewVideo = null
  let playbackFrame = null
  let scrollVersion = 0
  const pointerIntent = {
    x: 0,
    y: 0,
    movedAt: 0,
    scrollVersion: -1
  }

  const pausePreviewVideo = (video) => {
    video.pause()
    video.currentTime = 0
  }

  const playPreviewVideo = (video) => {
    video.muted = true
    video.play().catch(() => {
      // Browsers may still block autoplay in low-power or data-saving modes.
    })
  }

  const scorePreview = (item) => {
    if (item.ratio < 0.52) return 0
    const box = item.button.getBoundingClientRect()
    const centerX = box.left + box.width / 2
    const centerY = box.top + box.height / 2
    const viewportCenterX = window.innerWidth / 2
    const viewportCenterY = window.innerHeight / 2
    const distance = Math.hypot(centerX - viewportCenterX, centerY - viewportCenterY)
    const maxDistance = Math.hypot(viewportCenterX, viewportCenterY)
    const centerScore = 1 - Math.min(1, distance / maxDistance)
    const pointerIsFresh = Date.now() - pointerIntent.movedAt < 2600 && pointerIntent.scrollVersion === scrollVersion
    const pointerDistanceX = Math.max(box.left - pointerIntent.x, 0, pointerIntent.x - box.right)
    const pointerDistanceY = Math.max(box.top - pointerIntent.y, 0, pointerIntent.y - box.bottom)
    const pointerDistance = Math.hypot(pointerDistanceX, pointerDistanceY)
    const pointerScore = pointerIsFresh ? 1 - Math.min(1, pointerDistance / 180) : 0
    const focusScore = Date.now() - item.lastFocusAt < 2600 ? 1 : 0

    return item.ratio * 3 + centerScore * 2 + pointerScore * 4 + focusScore * 2
  }

  const chooseActivePreview = () => {
    playbackFrame = null

    if (document.hidden || document.querySelector(".gallery-modal.is-open")) {
      activePreviewVideo = null
      previewVideos.forEach(pausePreviewVideo)
      return
    }

    const best = previewItems
      .map((item) => ({item, score: scorePreview(item)}))
      .filter(({score}) => score > 0)
      .sort((a, b) => b.score - a.score)[0]?.item
    const nextVideo = best?.video || null

    if (nextVideo === activePreviewVideo) return
    activePreviewVideo = nextVideo
    previewVideos.forEach((video) => {
      if (video === activePreviewVideo) playPreviewVideo(video)
      else pausePreviewVideo(video)
    })
  }

  const schedulePreviewChoice = () => {
    if (playbackFrame) return
    playbackFrame = requestAnimationFrame(chooseActivePreview)
  }

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const item = previewItems.find((candidate) => candidate.video === entry.target)
        if (item) item.ratio = entry.isIntersecting ? entry.intersectionRatio : 0
      })
      schedulePreviewChoice()
    }, {
      threshold: [0, 0.25, 0.5, 0.65, 0.8, 0.92, 1]
    })

    previewVideos.forEach((video) => observer.observe(video))
    window.addEventListener("scroll", () => {
      scrollVersion += 1
      schedulePreviewChoice()
    }, {passive: true})
    window.addEventListener("resize", schedulePreviewChoice)
    document.addEventListener("visibilitychange", schedulePreviewChoice)
  } else {
    previewVideos.forEach((video) => {
      video.setAttribute("controls", "")
    })
  }

  const modal = document.createElement("div")
  modal.className = "gallery-modal"
  modal.setAttribute("role", "dialog")
  modal.setAttribute("aria-modal", "true")
  modal.setAttribute("aria-hidden", "true")
  modal.innerHTML = `
    <button class="gallery-modal-backdrop" type="button" aria-label="Close media preview"></button>
    <div class="gallery-modal-panel" role="document">
      <button class="gallery-modal-close" type="button" aria-label="Close media preview">&times;</button>
      <div class="gallery-modal-media"></div>
      <div class="gallery-modal-copy">
        <div>
          <h2></h2>
          <p></p>
        </div>
        <a class="button-secondary button-secondary-light" href="" target="_blank" rel="noopener noreferrer">Open in gallery</a>
      </div>
    </div>
  `
  document.body.appendChild(modal)

  const media = modal.querySelector(".gallery-modal-media")
  const title = modal.querySelector("h2")
  const summary = modal.querySelector("p")
  const galleryLink = modal.querySelector("a")
  const closeButtons = modal.querySelectorAll(".gallery-modal-close, .gallery-modal-backdrop")
  let previousFocus = null

  const closeModal = () => {
    modal.classList.remove("is-open")
    modal.setAttribute("aria-hidden", "true")
    media.innerHTML = ""
    document.body.style.overflow = ""

    if (previousFocus) {
      previousFocus.focus()
    }
  }

  const openModal = (button) => {
    const clip = button.dataset.modalClip
    const poster = button.dataset.modalPoster
    const isGif = clip.endsWith(".gif")
    previousFocus = button
    previewVideos.forEach(pausePreviewVideo)

    title.textContent = button.dataset.modalTitle
    summary.textContent = button.dataset.modalSummary
    galleryLink.href = button.dataset.modalGallery
    media.innerHTML = isGif
      ? `<img src="${clip}" alt="" />`
      : `<video controls autoplay muted playsinline poster="${poster}"><source src="${clip}" type="${clip.endsWith(".webm") ? "video/webm" : "video/mp4"}" /></video>`

    modal.classList.add("is-open")
    modal.setAttribute("aria-hidden", "false")
    document.body.style.overflow = "hidden"
    modal.querySelector(".gallery-modal-close").focus()
  }

  previewButtons.forEach((button) => {
    button.addEventListener("focus", () => {
      const item = previewItems.find((candidate) => candidate.button === button)
      if (item) {
        item.lastFocusAt = Date.now()
        schedulePreviewChoice()
      }
    })
    button.addEventListener("click", () => openModal(button))
  })

  window.addEventListener("pointermove", (event) => {
    if (!event.isPrimary) return
    pointerIntent.x = event.clientX
    pointerIntent.y = event.clientY
    pointerIntent.movedAt = Date.now()
    pointerIntent.scrollVersion = scrollVersion
    schedulePreviewChoice()
  }, {passive: true})

  closeButtons.forEach((button) => {
    button.addEventListener("click", closeModal)
  })

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && modal.classList.contains("is-open")) {
      closeModal()
    }
  })
}
